import { FC, useEffect, useRef, useState } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import { Header } from '../Header/Header';
import { Breadcrumbs } from './Breadcrumbs/Breadcrumbs';
import { ProductFamily } from './ProductFamily/ProductFamily';
import { ProductChoice } from './ProductChoice/ProductChoice';
import { apiPostAgreement } from '../../api/api';
import {
  retrieveCmsProduct,
  findSelectedCategoryByKey,
  findSelectedSubcategoryByKey,
  getPriceSum,
} from '../../helper/cms';
import { Conditional } from '../Common/Conditional';
import { SummaryStep } from './Summary/SummaryStep';
import { ConfirmationDialog } from './ConfirmationDialog';
import { Information } from './InformationStep/Information';
import { Invoice } from './InvoiceStep/Invoice';
import { Contact } from './ContactStep/Contact';
import dayjs from 'dayjs';
import { DEFAULT_LANGUAGE } from '../Header/LanguageDropdown';
import { AddonListActions, Product, ProductListActions, Steps } from '../../types/webshop';
import { LandingPage } from './LandingPage/LandingPage';
import { SigningPage } from './SigningPage/SigningPage';
import { Addon, Language, ShopContent } from '../../types/cms_shared_types';
import {
  ContactFormInputs,
  InformationFormInputs,
  InvoiceFormInputs,
  InvoiceMethod,
  ShopForm,
} from '../../types/api_shared_types';
import { addSpaceDelimiter } from '../../helper/helper';
import { trackEvent } from '../../helper/analytics';
import { Addons } from './Addons/Addons';
import { PriceSummary } from './Summary/PriceSummary';
import { ResetProcessConfirmationModal } from './ShoppingCart/ResetProcessConfirmationModal';
import { useResetConfirmationModal } from '../../contexts/ResetConfirmationModalContext';
import { Snackbar } from './Snackbar/Snackbar';

export const SELECTED_CATEGORY_KEY = 'selected_category_key';

const INVALIDATE_CACHE_DAYS = 1;

interface WebshopFormProps {
  selectedProducts: Product[];
  selectedProductsActions: ProductListActions;
  selectedAddons: Addon[];
  selectedAddonsActions: AddonListActions;
  cmsShopContent: ShopContent;
  referenceNumber: string | null;
}

export const WebshopForm: FC<WebshopFormProps> = ({
  selectedProducts,
  selectedProductsActions,
  selectedAddons,
  selectedAddonsActions,
  cmsShopContent,
  referenceNumber,
}) => {
  const [language] = useLocalStorageState<string>('language', { defaultValue: DEFAULT_LANGUAGE });

  const [currentStep, setCurrentStep, { removeItem: removeCurrentStep }] = useLocalStorageState<Steps>('current_step', {
    defaultValue: Steps.PRODUCT_FAMILY,
  });
  const [addonsDisplayUpdatedPricing, setAddonsDisplayUpdatedPricing] = useState(false);
  const [emptyCartSilently, setEmptyCartSilently] = useState(false);

  const [price, setPrice] = useState('');
  const [vatPrice, setVatPrice] = useState('');

  useEffect(() => {
    const { sumPrice, sumPriceVat } = getPriceSum(selectedProducts, selectedAddons);
    setPrice(addSpaceDelimiter(Math.ceil(sumPrice).toString()));
    setVatPrice(addSpaceDelimiter(Math.ceil(sumPriceVat - sumPrice).toString()));

    if (currentStep === Steps.SUMMARY && selectedProducts.length === 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [selectedProducts, currentStep, setCurrentStep, selectedAddons]);

  const [selectedCategoryKey, setSelectedCategoryKey, { removeItem: removeSelectedCategoryKey }] = useLocalStorageState<
    string | undefined
  >(SELECTED_CATEGORY_KEY, { defaultValue: undefined });

  const [selectedSubcategoryKey, setSelectedSubcategoryKey, { removeItem: removeSelectedSubcategoryKey }] =
    useLocalStorageState<string | undefined>('selected_subcategory_key', { defaultValue: undefined });

  const [selectedAddonCategoryKey, setSelectedAddonCategoryKey, { removeItem: removeSelectedAddonCategoryKey }] =
    useLocalStorageState<string | undefined>('selected_addon_category_key', { defaultValue: undefined });

  const [contractId, setContractId] = useState<number>();
  const [contractAccessLink, setContractAccessLink] = useState('');

  const signingCancelled = useRef(false);
  const onBackRef = useRef<(() => void) | null>(null);
  const [signingErrorOccurred, setSigningErrorOccurred, { removeItem: removeSigningErrorOccurred }] =
    useLocalStorageState<boolean>('signing_error', { defaultValue: false });

  const [lastVisitDate, setLastVisitDate, { removeItem: removeLastVisitDate }] = useLocalStorageState<string>(
    'last_visit_date',
    { defaultValue: dayjs().toString() },
  );

  const { showResetConfirmationModal, closeResetConfirmationModal, setShowResetConfirmationModal } =
    useResetConfirmationModal();

  const displayAddonsStep = (): boolean => {
    if (!cmsShopContent || !selectedCategoryKey) {
      return true;
    }

    const category = findSelectedCategoryByKey(cmsShopContent.categories, selectedCategoryKey);

    if (!category) {
      return true;
    }

    if (!category.enableAddons) {
      return false;
    }

    if (atLeastOneReportingProduct()) {
      return false;
    }

    return true;
  };

  const resetLocalStorage = (keepCategory = false) => {
    // eslint-disable-next-line no-console
    console.warn('resetting local storage');
    setCurrentStep(Steps.PRODUCT_FAMILY);
    if (!keepCategory) {
      setSelectedCategoryKey(undefined);
    }
    setSelectedSubcategoryKey(undefined);
    setSelectedAddonCategoryKey(undefined);
    setContractId(undefined);
    setAddonsDisplayUpdatedPricing(false);
    selectedProductsActions.reset();
    selectedAddonsActions.reset();
  };

  const clearLocalStorage = (keepCurrentStepInfo = true) => {
    if (keepCurrentStepInfo) {
      removeCurrentStep();
      removeSigningErrorOccurred();
    }
    removeSelectedCategoryKey();
    removeSelectedSubcategoryKey();
    removeSummaryFormComment();
    removeInformationFormInputs();
    removeInvoiceFormInputs();
    removeContactFormInputs();
    removeLastVisitDate();
    removeSelectedAddonCategoryKey();
    selectedProductsActions.reset();
    selectedAddonsActions.reset();
  };

  useEffect(() => {
    // Check if we should invalidate the local storage
    if (lastVisitDate) {
      const lastVisit = dayjs(lastVisitDate);
      const diff = dayjs().diff(lastVisit, 'day');

      if (diff > INVALIDATE_CACHE_DAYS) {
        setEmptyCartSilently(true);
        clearLocalStorage();
      }

      setLastVisitDate(dayjs().toString());
    }

    // If either the category key or the subcategory key are no longer valid (either because they
    // were manually changed locally, or because the actual keys have been changed in the CMS),
    // we reset the local storage
    if (selectedCategoryKey) {
      const category = findSelectedCategoryByKey(cmsShopContent.categories, selectedCategoryKey);
      if (category) {
        const subcategory = selectedSubcategoryKey
          ? findSelectedSubcategoryByKey(selectedSubcategoryKey, category.subcategories)
          : undefined;
        if (subcategory && selectedProducts.length > 0) {
          return;
        }
        resetLocalStorage(true);
        setCurrentStep(Steps.PRODUCT_FAMILY);
        return;
      }
    }

    resetLocalStorage();
    setCurrentStep(Steps.PRODUCT_FAMILY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [summaryFormComment, setSummaryFormComment, { removeItem: removeSummaryFormComment }] =
    useLocalStorageState<string>('summary_form_comment', {
      defaultValue: '',
    });

  const [informationFormInputs, setInformationFormInputs, { removeItem: removeInformationFormInputs }] =
    useLocalStorageState<InformationFormInputs>('information_form_inputs', {
      defaultValue: {
        businessNameInput: '',
        businessCategoryInput: '',
        businessAddressInput: '',
        businessPostNumberInput: '',
        businessCityInput: '',
      },
    });

  const [invoiceFormInputs, setInvoiceFormInputs, { removeItem: removeInvoiceFormInputs }] =
    useLocalStorageState<InvoiceFormInputs>('invoice_form_inputs', {
      defaultValue: {
        organisationNumberInput: '',
        companyNameInput: '',
        companyAddressInput: '',
        companyPostNumberInput: '',
        companyCityInput: '',
        companyCountryInput: '',
        sameInvoiceAddressInput: true,
        invoiceCareOfInput: '',
        invoiceAddressInput: '',
        invoicePostNumberInput: '',
        invoiceCityInput: '',
        invoiceCountryInput: '',
        startDateInput: dayjs().format('YYYY-MM-DD'),
        invoicePeriodInput: '',
        emailAddressPdfInput: '',
        selectedInvoiceMethod: InvoiceMethod.EMAIL,
        ediFormat: '',
      },
    });

  const [contactFormInputs, setContactFormInputs, { removeItem: removeContactFormInputs }] =
    useLocalStorageState<ContactFormInputs>('contact_form_inputs', {
      defaultValue: {
        contactFirstNameInput: '',
        contactLastNameInput: '',
        contactEmailInput: '',
        contactTelephoneNumberInput: '',
        soundtrackEmailInput: '',

        sameReportingContactInput: true,
        sameSoundtrackEmailInput: true,

        reportingFirstNameInput: '',
        reportingLastNameInput: '',
        reportingEmailInput: '',
        reportingTelephoneNumberInput: '',

        acceptTermsAndConditions: false,
      },
    });

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  useEffect(() => {
    setInvoiceFormInputs({ ...invoiceFormInputs, invoicePeriodInput: '' });
    setContactFormInputs({ ...contactFormInputs, sameReportingContactInput: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProducts]);

  useEffect(() => {
    if (!(selectedCategoryKey === 'butik' && selectedSubcategoryKey === 'butik')) {
      setInformationFormInputs({ ...informationFormInputs, businessCategoryInput: '' });
    }
  }, [selectedCategoryKey, selectedSubcategoryKey, informationFormInputs, setInformationFormInputs]);

  useEffect(() => {
    setSummaryFormComment('');
  }, [selectedSubcategoryKey, setSummaryFormComment]);

  const previousButtonClicked = () => {
    if (currentStep === Steps.PRODUCT_CHOICE && (selectedProducts.length > 0 || selectedAddons.length > 0)) {
      setShowConfirmationDialog(true);
      return;
    }

    if (currentStep === Steps.ADDONS && addonsDisplayUpdatedPricing) {
      setAddonsDisplayUpdatedPricing(false);
      return;
    }

    // Save form values before navigating back
    if (onBackRef.current) {
      onBackRef.current();
      onBackRef.current = null;
    }

    const stepDecrement = currentStep === Steps.INFORMATION && !displayAddonsStep() ? 2 : 1;
    setCurrentStep(currentStep - stepDecrement);

    if (currentStep === Steps.PRODUCT_CHOICE) {
      setSelectedSubcategoryKey(undefined);
    }
  };

  const nextButtonClicked = () => {
    const stepIncrement = currentStep === Steps.SUMMARY && !displayAddonsStep() ? 2 : 1;
    setCurrentStep(currentStep + stepIncrement);
  };

  const atLeastOneReportingProduct = () => {
    if (!selectedCategoryKey || !selectedSubcategoryKey) {
      return false;
    }

    return selectedProducts.some(
      (p) =>
        retrieveCmsProduct(p.code, cmsShopContent.categories, selectedCategoryKey, selectedSubcategoryKey)?.reporting,
    );
  };

  const render = () => {
    if (!cmsShopContent) {
      return;
    }

    const analyticsCategoryIndicator = `${selectedCategoryKey ?? ''}${
      selectedCategoryKey && selectedSubcategoryKey ? '::' : ''
    }${selectedSubcategoryKey ?? ''}`;

    if (currentStep === Steps.PRODUCT_FAMILY) {
      return (
        <ProductFamily
          selectedCategoryKey={selectedCategoryKey}
          setSelectedCategoryKey={setSelectedCategoryKey}
          nextButtonClicked={() => {
            trackEvent('SelectedCateogry', analyticsCategoryIndicator);
            nextButtonClicked();
          }}
          cmsShopContent={cmsShopContent}
        />
      );
    } else if (currentStep === Steps.PRODUCT_CHOICE) {
      const category = selectedCategoryKey
        ? findSelectedCategoryByKey(cmsShopContent.categories, selectedCategoryKey)
        : undefined;
      if (!category) {
        return <></>;
      }
      return (
        <ProductChoice
          category={category}
          selectedSubcategoryKey={selectedSubcategoryKey}
          setSelectedSubcategoryKey={setSelectedSubcategoryKey}
          selectedProducts={selectedProducts}
          selectedProductsActions={selectedProductsActions}
          nextButtonClicked={() => {
            trackEvent('SelectedSubcategory', analyticsCategoryIndicator);
            nextButtonClicked();
          }}
          cmsShopContent={cmsShopContent}
          selectedAddons={selectedAddons}
          selectedAddonsActions={selectedAddonsActions}
        />
      );
    } else if (currentStep === Steps.SUMMARY) {
      return (
        <SummaryStep
          selectedProducts={selectedProducts}
          selectedProductsActions={selectedProductsActions}
          selectedAddons={selectedAddons}
          selectedAddonsActions={selectedAddonsActions}
          price={price}
          vatPrice={vatPrice}
          selectedCategoryKey={selectedCategoryKey}
          selectedSubcategoryKey={selectedSubcategoryKey}
          summaryFormComment={summaryFormComment}
          setSummaryFormComment={setSummaryFormComment}
          nextButtonClicked={() => {
            trackEvent('ConfirmedPrice', analyticsCategoryIndicator);
            nextButtonClicked();
          }}
          cmsShopContent={cmsShopContent}
          registerOnBack={(callback) => {
            onBackRef.current = callback;
          }}
        />
      );
    } else if (currentStep === Steps.ADDONS) {
      return (
        <Addons
          selectedAddonCategoryKey={selectedAddonCategoryKey}
          setSelectedAddonCategoryKey={setSelectedAddonCategoryKey}
          cmsShopContent={cmsShopContent}
          nextButtonClicked={() => {
            trackEvent('SelectedAddons', analyticsCategoryIndicator);
            nextButtonClicked();
          }}
          selectedAddons={selectedAddons}
          selectedAddonsActions={selectedAddonsActions}
          getPriceSummary={() => (
            <PriceSummary
              selectedProducts={selectedProducts}
              selectedProductsActions={selectedProductsActions}
              selectedAddons={selectedAddons}
              selectedAddonsActions={selectedAddonsActions}
              price={price}
              vatPrice={vatPrice}
              selectedCategoryKey={selectedCategoryKey}
              selectedSubcategoryKey={selectedSubcategoryKey}
              cmsShopContent={cmsShopContent}
            />
          )}
          summaryFormComment={summaryFormComment}
          setSummaryFormComment={setSummaryFormComment}
          displayUpdatedPricing={addonsDisplayUpdatedPricing}
          setDisplayUpdatedPricing={setAddonsDisplayUpdatedPricing}
        />
      );
    } else if (currentStep === Steps.INFORMATION) {
      return (
        <Information
          setInformationFormInputs={setInformationFormInputs}
          informationFormInputs={informationFormInputs}
          hasSpecialShoppingProduct={selectedCategoryKey === 'butik' && selectedSubcategoryKey === 'butik'}
          nextButtonClicked={() => {
            trackEvent('SubmittedLocation', analyticsCategoryIndicator);
            nextButtonClicked();
          }}
          cmsShopContent={cmsShopContent}
          registerOnBack={(callback) => {
            onBackRef.current = callback;
          }}
        />
      );
    } else if (currentStep === Steps.INVOICE) {
      return (
        <Invoice
          price={price}
          selectedCategoryKey={selectedCategoryKey}
          selectedSubcategoryKey={selectedSubcategoryKey}
          invoiceFormInputs={invoiceFormInputs}
          setInvoiceFormInputs={setInvoiceFormInputs}
          atLeastOneReportingProduct={atLeastOneReportingProduct()}
          addonsForceMonthlyInvoicing={selectedAddons.some((addon) => addon.addonProvider.forceMonthlyInvoicing)}
          nextButtonClicked={() => {
            trackEvent('SubmittedPaymentDetails', analyticsCategoryIndicator);
            nextButtonClicked();
          }}
          cmsShopContent={cmsShopContent}
          language={language}
          registerOnBack={(callback) => {
            onBackRef.current = callback;
          }}
        />
      );
    } else if (currentStep === Steps.CONTACT) {
      return (
        <Contact
          selectedProducts={selectedProducts}
          selectedAddons={selectedAddons}
          selectedCategoryKey={selectedCategoryKey}
          selectedSubcategoryKey={selectedSubcategoryKey}
          comments={summaryFormComment}
          informationFormInputs={informationFormInputs}
          invoiceFormInputs={invoiceFormInputs}
          contactFormInputs={contactFormInputs}
          setContactFormInputs={setContactFormInputs}
          language={language as Language}
          startOneflowAgreementSigning={async (shopForm: ShopForm) => {
            signingCancelled.current = false;
            try {
              const res = await apiPostAgreement(shopForm);
              if (signingCancelled.current) {
                return;
              }
              setContractAccessLink(res.data.access_link);
              window.open(res.data.access_link, '_blank');

              const contractId = Number(res.data.contractId);
              setContractId(contractId);

              trackEvent('CreatedAgreement', analyticsCategoryIndicator);
            } catch {
              setSigningErrorOccurred(true);
              setCurrentStep(Steps.LANDING_PAGE);
              return;
            }
            setCurrentStep(Steps.SIGNING_PAGE);
          }}
          atLeastOneReportingProduct={atLeastOneReportingProduct()}
          cmsShopContent={cmsShopContent}
          referenceNumber={referenceNumber}
          registerOnBack={(callback) => {
            onBackRef.current = callback;
          }}
        />
      );
    } else if (currentStep === Steps.SIGNING_PAGE) {
      return (
        <SigningPage
          contractId={contractId}
          contractAccessLink={contractAccessLink}
          onCancelSigning={async () => {
            signingCancelled.current = true;
            setCurrentStep(Steps.CONTACT);
            setContractAccessLink('');
          }}
          cmsShopContent={cmsShopContent}
          setCurrentStep={setCurrentStep}
          setSigningErrorOccurred={setSigningErrorOccurred}
          analyticsCategoryIndicator={analyticsCategoryIndicator}
        />
      );
    } else if (currentStep === Steps.LANDING_PAGE) {
      return (
        <LandingPage
          isError={signingErrorOccurred}
          selectedAddons={selectedAddons}
          onSuccessButtonClick={() => {
            const url = cmsShopContent.landingPageStep.buttonLinkUrl;
            setEmptyCartSilently(true);
            clearLocalStorage(false);
            window.location.href = url;
          }}
          onErrorButtonClick={() => {
            setSigningErrorOccurred(false);
            setCurrentStep(Steps.CONTACT);
          }}
          cmsShopContent={cmsShopContent}
        />
      );
    } else {
      return;
    }
  };

  return (
    <div>
      <div>
        <Header cmsContent={cmsShopContent} />

        <Breadcrumbs
          currentStep={currentStep}
          selectedCategoryKey={selectedCategoryKey}
          selectedSubcategoryKey={selectedSubcategoryKey}
          price={price}
          vatPrice={vatPrice}
          selectedProducts={selectedProducts}
          selectedProductsActions={selectedProductsActions}
          selectedAddons={selectedAddons}
          selectedAddonsActions={selectedAddonsActions}
          onBackClick={previousButtonClicked}
          cmsShopContent={cmsShopContent}
          displayAddonsStep={displayAddonsStep}
        />

        {render()}
      </div>

      <Conditional
        condition={showConfirmationDialog}
        trueRender={
          <ConfirmationDialog
            showModal={true}
            setShowModal={setShowConfirmationDialog}
            onYesCallback={() => {
              selectedProductsActions.reset();
              selectedAddonsActions.reset();
              setSelectedSubcategoryKey(undefined);
              setCurrentStep(currentStep - 1);
            }}
            title={cmsShopContent.confirmationDialog.titleWhenSwitchingVerksamhetstyp}
            description={cmsShopContent.confirmationDialog.descriptionWhenSwitchingVerksamhetstyp}
            cmsContent={cmsShopContent}
          />
        }
      />
      <ResetProcessConfirmationModal
        cmsShopContent={cmsShopContent}
        showModal={showResetConfirmationModal}
        setShowModal={setShowResetConfirmationModal}
        resetProcess={() => {
          resetLocalStorage();
          closeResetConfirmationModal();
        }}
        addonsInCart={selectedAddons.length > 0}
      />
      <Snackbar
        shopContent={cmsShopContent}
        selectedProducts={selectedProducts}
        selectedAddons={selectedAddons}
        emptyCartSilently={emptyCartSilently}
        resetEmptyCartSilently={() => setEmptyCartSilently(false)}
      />
    </div>
  );
};
